import { getZoomFactor } from './zoomPopper';

/**
 * Adjusts a MUI Popover / Menu paper element's top and left styles
 * to compensate for the global CSS zoom factor on document.documentElement.
 */
export const adjustPopoverPosition = (element: HTMLElement, zoom: number = getZoomFactor()): void => {
  if (zoom === 1 || !element || !element.style) return;

  const rawTopStr = element.style.top;
  const rawLeftStr = element.style.left;
  if (!rawTopStr || !rawLeftStr) return;

  const currentTop = parseFloat(rawTopStr);
  const currentLeft = parseFloat(rawLeftStr);
  if (isNaN(currentTop) || isNaN(currentLeft)) return;

  // Avoid re-adjusting already adjusted values
  const lastTop = (element as any)._zoomAdjustedTop;
  const lastLeft = (element as any)._zoomAdjustedLeft;
  if (lastTop === currentTop && lastLeft === currentLeft) {
    return;
  }

  console.log('[zoomPopover] Adjusting element:', {
    classes: element.className,
    rawTopStr,
    rawLeftStr,
    currentTop,
    currentLeft,
    lastTop,
    lastLeft,
    zoom,
  });

  // Parse transformOrigin to compute accurate origin offset
  const transformOrigin = element.style.transformOrigin || '';
  const parts = transformOrigin.split(' ').filter(Boolean);

  let originX = 0;
  let originY = 0;

  const elemW = element.offsetWidth || element.clientWidth || 0;
  const elemH = element.offsetHeight || element.clientHeight || 0;

  if (parts.length >= 1) {
    const p0 = parts[0];
    if (p0.endsWith('px')) {
      originX = parseFloat(p0);
    } else if (p0 === 'center') {
      originX = elemW / 2;
    } else if (p0 === 'right') {
      originX = elemW;
    }
  }

  if (parts.length >= 2) {
    const p1 = parts[1];
    if (p1.endsWith('px')) {
      originY = parseFloat(p1);
    } else if (p1 === 'center') {
      originY = elemH / 2;
    } else if (p1 === 'bottom') {
      originY = elemH;
    }
  }

  // MUI Popover computes:
  // rawTop = anchorOffset.top - originY
  // rawLeft = anchorOffset.left - originX
  // Since anchorOffset was in visual pixels (scaled by zoom),
  // we scale the anchorOffset back to layout coordinates by dividing by zoom.
  const adjustedTop = Math.round((currentTop + originY) / zoom - originY);
  const adjustedLeft = Math.round((currentLeft + originX) / zoom - originX);

  (element as any)._zoomAdjustedTop = adjustedTop;
  (element as any)._zoomAdjustedLeft = adjustedLeft;

  element.style.top = `${adjustedTop}px`;
  element.style.left = `${adjustedLeft}px`;
};

/**
 * Adjusts a MUI Tabs indicator element's left and width styles
 * to align perfectly under CSS zoom.
 */
export const adjustTabsIndicatorStyle = (element: HTMLElement, zoom: number = getZoomFactor()): void => {
  if (zoom === 1 || !element || !element.style) return;

  const rawLeftStr = element.style.left;
  const rawWidthStr = element.style.width;

  if (!rawLeftStr || !rawWidthStr) return;

  const currentLeft = parseFloat(rawLeftStr);
  const currentWidth = parseFloat(rawWidthStr);

  if (isNaN(currentLeft) || isNaN(currentWidth)) return;

  // Avoid re-adjusting already adjusted values
  const lastLeft = (element as any)._zoomAdjustedLeft;
  const lastWidth = (element as any)._zoomAdjustedWidth;
  if (lastLeft === currentLeft && lastWidth === currentWidth) {
    return;
  }

  const adjustedLeft = Math.round(currentLeft / zoom);
  const adjustedWidth = Math.round(currentWidth / zoom);

  (element as any)._zoomAdjustedLeft = adjustedLeft;
  (element as any)._zoomAdjustedWidth = adjustedWidth;

  element.style.left = `${adjustedLeft}px`;
  element.style.width = `${adjustedWidth}px`;
};

/**
 * Adjusts Google Places Autocomplete dropdown (.pac-container) styles
 * (top, left, width, maxHeight) to strictly enforce positioning below the parent input
 * without ever flipping above or overlapping.
 */
export const adjustPacContainer = (element: HTMLElement, zoom: number = getZoomFactor()): void => {
  if (zoom === 1 || !element || !element.style) return;

  const isHidden = element.style.display === 'none';
  if (isHidden) return;

  // Find the active or matching input that owns this pac-container
  let targetInput: HTMLInputElement | null = null;
  const activeEl = document.activeElement as HTMLElement | null;

  if (activeEl && activeEl.tagName === 'INPUT') {
    targetInput = activeEl as HTMLInputElement;
  } else {
    // Look for Google address input or any matching input on the screen
    const inputs = document.querySelectorAll<HTMLInputElement>('input.google-address-input, input');
    const rawLeft = parseFloat(element.style.left || '0');
    const expectedVisualLeft = !isNaN(rawLeft) ? rawLeft * zoom : null;

    for (let i = 0; i < inputs.length; i++) {
      const inp = inputs[i];
      const r = inp.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        if (inp.classList.contains('google-address-input')) {
          targetInput = inp;
          break;
        }
        if (expectedVisualLeft !== null && Math.abs(r.left - expectedVisualLeft) < 50) {
          targetInput = inp;
          break;
        }
      }
    }
  }

  const vh = window.innerHeight || document.documentElement.clientHeight;
  const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
  const scrollX = window.scrollX || document.documentElement.scrollLeft || 0;

  if (targetInput) {
    const inputRect = targetInput.getBoundingClientRect();

    // If the input is scrolled offscreen or hidden, hide pac-container
    if (inputRect.width === 0 || inputRect.height === 0 || inputRect.bottom < 0 || inputRect.top > vh) {
      element.style.display = 'none';
      return;
    }

    // STRICT RULE: The dropdown must ALWAYS start directly below the input's bottom edge (never above or overlapping)
    const layoutTop = Number(((inputRect.bottom + 2) / zoom + scrollY).toFixed(2));
    const layoutLeft = Number((inputRect.left / zoom + scrollX).toFixed(2));
    const layoutWidth = Number((inputRect.width / zoom).toFixed(2));

    const viewportWidth = (window.innerWidth || document.documentElement.clientWidth) / zoom;
    const maxWidth = Math.max(200, Math.min(layoutWidth, viewportWidth - 32));
    const safeLeft = Math.max(8, Math.min(layoutLeft, viewportWidth - maxWidth - 16));

    // Limit height to available viewport space below the input so it never overflows offscreen
    const spaceBelow = Math.max(120, (vh - inputRect.bottom - 20) / zoom);
    const maxHeight = Math.min(280, spaceBelow);

    const currentActualTop = parseFloat(element.style.top);
    const currentActualLeft = parseFloat(element.style.left);
    const currentActualWidth = parseFloat(element.style.width);

    // If actual inline styles already match the desired layout coordinates, do nothing
    if (currentActualTop === layoutTop && currentActualLeft === safeLeft && currentActualWidth === maxWidth) {
      return;
    }

    element.style.top = `${layoutTop}px`;
    element.style.left = `${safeLeft}px`;
    element.style.width = `${maxWidth}px`;
    element.style.maxHeight = `${maxHeight}px`;
    element.style.overflowY = 'auto';
    return;
  }

  // Fallback if no target input was located
  const rawTopStr = element.style.top;
  const rawLeftStr = element.style.left;
  const rawWidthStr = element.style.width;

  if (!rawTopStr || !rawLeftStr) return;

  const currentTop = parseFloat(rawTopStr);
  const currentLeft = parseFloat(rawLeftStr);
  const currentWidth = parseFloat(rawWidthStr);

  if (isNaN(currentTop) || isNaN(currentLeft)) return;

  const adjustedTop = Number((currentTop / zoom).toFixed(2));
  const adjustedLeft = Number((currentLeft / zoom).toFixed(2));
  const adjustedWidth = !isNaN(currentWidth) ? Number((currentWidth / zoom).toFixed(2)) : undefined;

  if (
    currentTop === adjustedTop &&
    currentLeft === adjustedLeft &&
    (!adjustedWidth || currentWidth === adjustedWidth)
  ) {
    return;
  }

  element.style.top = `${adjustedTop}px`;
  element.style.left = `${adjustedLeft}px`;
  if (adjustedWidth !== undefined) {
    element.style.width = `${adjustedWidth}px`;
  }
  element.style.maxHeight = '280px';
  element.style.overflowY = 'auto';
};

let observer: MutationObserver | null = null;
let isInitialized = false;

/**
 * Initializes a global DOM observer that intercepts and corrects positioning for all
 * MUI Popovers, Menus, Select dropdowns, DatePickers, Tabs indicators, and Google Places dropdowns under CSS zoom.
 */
export const initZoomPopover = (): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return;
  }
  if (isInitialized) {
    return;
  }
  isInitialized = true;

  const handleReposition = () => {
    const activePopovers = document.querySelectorAll<HTMLElement>(
      '.MuiPopover-paper, .MuiMenu-paper, .zoom-adjusted-menu-paper',
    );
    for (let i = 0; i < activePopovers.length; i++) {
      adjustPopoverPosition(activePopovers[i]);
    }
    const activeIndicators = document.querySelectorAll<HTMLElement>('.MuiTabs-indicator');
    for (let i = 0; i < activeIndicators.length; i++) {
      adjustTabsIndicatorStyle(activeIndicators[i]);
    }
    const pacContainers = document.querySelectorAll<HTMLElement>('.pac-container');
    for (let i = 0; i < pacContainers.length; i++) {
      adjustPacContainer(pacContainers[i]);
    }
  };

  observer = new MutationObserver(mutations => {
    let needsAdjustment = false;
    for (let i = 0; i < mutations.length; i++) {
      const mutation = mutations[i];
      if (mutation.type === 'childList') {
        needsAdjustment = true;
        break;
      } else if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
        const target = mutation.target as HTMLElement;
        if (
          target &&
          target.classList &&
          (target.classList.contains('MuiPopover-paper') ||
            target.classList.contains('MuiMenu-paper') ||
            target.classList.contains('zoom-adjusted-menu-paper') ||
            target.classList.contains('MuiTabs-indicator') ||
            target.classList.contains('pac-container'))
        ) {
          needsAdjustment = true;
          break;
        }
      }
    }

    if (needsAdjustment) {
      handleReposition();
    }
  });

  const startObserving = () => {
    if (document.body) {
      observer!.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
      handleReposition();
    }
  };

  if (document.body) {
    startObserving();
  } else {
    window.addEventListener('DOMContentLoaded', startObserving, { once: true });
  }

  window.addEventListener('resize', handleReposition, { passive: true });
  window.addEventListener('scroll', handleReposition, { capture: true, passive: true });
  window.addEventListener('input', handleReposition, { capture: true, passive: true });
  window.addEventListener('keyup', handleReposition, { capture: true, passive: true });
};

// Automatically initialize in browser environments
if (typeof window !== 'undefined') {
  initZoomPopover();
}
